import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { Placeholder } from "components/Placeholder";
import { OneTimeSeatWarningDialog } from "components/OneTimeSeatWarningDialog";
import { SearchField } from "components/SearchField";
import {
  ServiceSummary,
  SortOrder,
  User,
  UserFilters,
  UserWithClaims,
} from "data/interfaces";
import { useEffect, useState } from "react";
import { AssignUserRow } from "./AssignUserRow";
import {
  getClaimDescription,
  isShowDropdown,
  sortUsersBy,
} from "../../data/functions";

interface AssignUsersDialogProps {
  users: User[];
  selectedService: ServiceSummary;
  onClose: () => void;
  onConfirm: (assignUserRequest: UserWithClaims[]) => void;
}

const AssignUsersDialog = ({
  users,
  selectedService,
  onClose,
  onConfirm,
}: AssignUsersDialogProps) => {
  const [assignUserRequests, setAssignUserRequests] = useState<
    UserWithClaims[]
  >([]);
  const [hideAssignedUser, setHideAssignedUser] = useState(false);
  const [availableSeats, setAvailableSeats] = useState<number>(
    selectedService.seatsTotal - selectedService.seatsUsed,
  );
  const [order, setOrder] = useState<SortOrder>("asc");
  const [warningOpen, setWarningOpen] = useState(false);
  const [warningType, setWarningType] = useState<"assign" | "unassign">(
    "assign",
  );

  const sortedUsers = sortUsersBy("name", order, users);

  const filteredAssignedUsers = hideAssignedUser
    ? sortedUsers.filter(
        (user) =>
          user.services.findIndex(
            (s) => s.licenseReference === selectedService.licenseReference,
          ) === -1,
      )
    : sortedUsers;

  const [filters, setFilters] = useState<UserFilters>({ search: "" });
  const filteredUsers = filteredAssignedUsers.filter((user) => {
    let searchString = filters.search?.toLowerCase();
    return (
      searchString == null ||
      (user.name || "").toLowerCase().includes(searchString) ||
      user.email.toLowerCase().includes(searchString)
    );
  });

  useEffect(() => {
    const assignedUsers = users.filter(
      (user) =>
        user.services.findIndex(
          (s) => s.licenseReference === selectedService.licenseReference,
        ) !== -1,
    );
    const newAssignRequests = assignUserRequests.filter(
      (r) => !assignedUsers.includes(r.user) && r.claims.length > 0,
    );
    setAvailableSeats(
      selectedService.seatsTotal -
        selectedService.seatsUsed -
        newAssignRequests.length,
    );
  }, [assignUserRequests, selectedService, users]);

  const handleFilterChange = (filters: UserFilters) => {
    setFilters(filters);
  };

  const addRequest = (request: UserWithClaims) => {
    setAssignUserRequests([
      ...assignUserRequests.filter((r) =>
        r.user.id !== null
          ? r.user.id !== request.user.id
          : r.user.email !== request.user.email,
      ),
      request,
    ]);
  };

  const showDropdown = isShowDropdown(selectedService);

  const handleAssignUsers = (assignUserRequests: UserWithClaims[]): void => {
    if (selectedService.oneTimeSeats && assignUserRequests.length > 0) {
      const hasUnassignments = assignUserRequests.some(
        (r) =>
          r.claims.length === 0 &&
          users
            .find((u) =>
              u.id !== null ? u.id === r.user.id : u.email === r.user.email,
            )
            ?.services.some(
              (s) => s.licenseReference === selectedService.licenseReference,
            ),
      );
      setWarningType(hasUnassignments ? "unassign" : "assign");
      setWarningOpen(true);
    } else {
      onConfirm(assignUserRequests);
    }
  };

  return (
    <Dialog open={true} onClose={onClose} maxWidth="md">
      <DialogTitle>
        Assign/Edit users to the service
        {selectedService.unlimitedSeats === false && availableSeats >= 0 && (
          <Typography sx={{ py: 1, fontWeight: "bold" }}>
            {availableSeats}
            {" seat(s) available"}
          </Typography>
        )}
        {selectedService.unlimitedSeats === false && availableSeats < 0 && (
          <Typography color="error" sx={{ py: 1, fontWeight: "bold" }}>
            No more seats available
          </Typography>
        )}
        <SearchField
          value={filters.search}
          fullWidth={true}
          onChange={(event: any) =>
            handleFilterChange({
              ...filters,
              search: event.target.value,
            })
          }
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={hideAssignedUser}
              onChange={(event: any) =>
                setHideAssignedUser(event.target.checked)
              }
            />
          }
          label="Hide assigned users"
        />
      </DialogTitle>
      <DialogContent>
        <TableContainer sx={{ height: "600px" }}>
          {filteredUsers.length === 0 ? (
            <Placeholder>No users found</Placeholder>
          ) : (
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={true}
                      direction={order}
                      onClick={() => setOrder(order === "asc" ? "desc" : "asc")}
                    >
                      Name
                    </TableSortLabel>
                  </TableCell>
                  {!showDropdown &&
                    selectedService.serviceClaims.map((claim, claimIndex) =>
                      claim.options.length >= 1 ? (
                        claim.options.map((option, optionIndex) => (
                          <TableCell key={optionIndex} align="center">
                            {getClaimDescription(claim, option)}
                          </TableCell>
                        ))
                      ) : (
                        <TableCell key={claimIndex} align="center">
                          {claim.shortName}
                        </TableCell>
                      ),
                    )}
                  {showDropdown && <TableCell padding="checkbox"></TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.map((user) => (
                  <AssignUserRow
                    key={user.id ?? `unregistered-${user.email}`}
                    showDropdown={showDropdown}
                    user={user}
                    selectedService={selectedService}
                    assignUserRequest={assignUserRequests.find((r) =>
                      r.user.id !== null
                        ? r.user.id === user.id
                        : r.user.email === user.email,
                    )}
                    addRequest={addRequest}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="outlined"
          disabled={
            assignUserRequests.length === 0 ||
            (selectedService.unlimitedSeats === false && availableSeats < 0)
          }
          onClick={() => handleAssignUsers(assignUserRequests)}
        >
          Assign
        </Button>
      </DialogActions>
      <OneTimeSeatWarningDialog
        open={warningOpen}
        onClose={() => setWarningOpen(false)}
        onConfirm={() => {
          setWarningOpen(false);
          onConfirm(assignUserRequests);
        }}
        variant={warningType}
      />
    </Dialog>
  );
};

export default AssignUsersDialog;
